/**
 * Created by jv on 2019-08-22 (11:36).
 */
import utils from "Sources/utils/utils";
import AppError from "Sources/models/app-error";
import CONSTANTS from "Sources/config/constants";
import RoadUtils from "Sources/components/business-model/road-utils";

const log = require("Sources/utils/logger")();

class RoadMap {
  constructor(options) {
    if (utils.isNull(options)) throw new AppError({exception: new Error("Options for BusinessModel cannot be null")});

    this.businessModel = options.businessModel;

    this.id = this.businessModel.id;

    this.containerElement = this.businessModel.containerElement;
    this.layoutData = this.businessModel.layoutData;

    this.canvas = this.businessModel.canvas;
    this.roadMapGroup = null;
    this.crossingsGroup = null;

    this.arrowGroups = {}; // Not a fabric obj

    this.intersections = [];
    this.roads = [];

    this._templateRoadArrow = null;
    this.activeConnections = [];
    this.activeRoadsGroup = null;

    this.previewGroup = null;

    this.animationsRunning = 0;
    this.shouldAbortAnimations = false;

    this.onRenderComplete = options.onRenderComplete;
  }

  render() {
    this.roadMapGroup = new fabric.Group([], {
      hasBorders: false,
      hasControls: false,
      selectable: false,
      evented: false,
      reference: false,
      movable: false
    });

    this.previewGroup = new fabric.Group([], {
      hasBorders: false,
      hasControls: false,
      selectable: false,
      evented: false,
      reference: false,
      movable: false
    });

    this.crossingsGroup = new fabric.Group([], {
      hasBorders: false,
      hasControls: false,
      selectable: false,
      evented: false,
      reference: false
    });

    this.canvas.add(this.roadMapGroup);
    this.canvas.add(this.previewGroup);
    this.canvas.add(this.crossingsGroup);

    for (let i = 0; i < this.layoutData.roads.length; i++) {
      this.drawRoad(this.layoutData.roads[i])
        .then((roadGroup) => {
          this._checkRenderComplete();
        });
    }

    for (let intersectionKey in this.layoutData.intersections) {
      if (this.layoutData.intersections.hasOwnProperty(intersectionKey)) {
        this.drawIntersection(this.layoutData.intersections[intersectionKey], intersectionKey);
      }
    }

    this.activeRoadsGroup = new fabric.Group([], {
      hasBorders: false,
      hasControls: false,
      selectable: false,
      evented: false,
      reference: false,
      movable: false,
      objectCaching: false
    });

    this.canvas.add(this.activeRoadsGroup);

    this.businessModel.loadSvg(this.layoutData.roadSettings.activeArrow)
      .then((fabricObj) => {
        this._templateRoadArrow = fabricObj;
        this._templateRoadArrow.left = 0;
        this._templateRoadArrow.top = 0;
        this._templateRoadArrow.visible = false;
        this._templateRoadArrow.originX = "center";
        this._templateRoadArrow.originY = "center";


        this.canvas.add(this._templateRoadArrow);
        this._templateRoadArrow.scaleToHeight(this.layoutData.roadSettings.dividerWidth * 2.5);

        this.drawArrows();

        this._checkRenderComplete();
      })
      .catch((err) => {
        log.error("Failed to load template arrow for active road - %o", err);
      });

    this._checkRenderComplete();
  }

  _checkRenderComplete() {
    if (this.hasRendered() && utils.isFunction(this.onRenderComplete)) {
      this.roadMapGroup.addWithUpdate();
      this.roadMapGroup.setCoords();

      this.setUpCanvasLayer();

      this.canvas.renderAll();

      this.onRenderComplete.call(this);
    }
  }

  setUpCanvasLayer() {
    this.canvas.bringToFront(this.activeRoadsGroup);
    this.canvas.bringToFront(this.crossingsGroup);

    for (let group in this.arrowGroups) {
      if (this.arrowGroups.hasOwnProperty(group)) {
        this.canvas.bringToFront(this.arrowGroups[group].fabricObj);
      }
    }
  }

  hasRendered() {
    let intersectionsRendered = Object.keys(this.layoutData.intersections).length === this.intersections.length;
    let roadsRendered = this.layoutData.roads.length === this.roads.length;
    let templateArrowRendered = utils.isNotNull(this._templateRoadArrow);

    // log.debug("Intersections: %d/%d (rendered/required)\nRoads: %d/%d (rendered/required)", this.intersections.length, Object.keys(this.layoutData.intersections).length, this.roads.length, this.layoutData.roads.length);

    return intersectionsRendered && roadsRendered && templateArrowRendered;
  }

  drawIntersection(intersection, key) {
    this.businessModel.drawDebugInfo(key, intersection.position, "#FFAA00");

    let intersectionGroup = {
      data: intersection
    };

    let dividerWidth = this.layoutData.roadSettings.dividerWidth,
      backgroundSize = this.layoutData.roadSettings.innerWidth,
      outerSize = this.layoutData.roadSettings.outerWidth,
      strokeWidth = (outerSize - backgroundSize) / 2;

    let strokePathString = "";
    let dividerPathString = "";

    let intersectionTop = intersection.position.y - (backgroundSize / 2) - (strokeWidth / 2);
    let intersectionRight = intersection.position.x + (backgroundSize / 2) + (strokeWidth / 2);
    let intersectionBottom = intersection.position.y + (backgroundSize / 2) + (strokeWidth / 2);
    let intersectionLeft = intersection.position.x - (backgroundSize / 2) - (strokeWidth / 2);

    let intersectionCenter = {
      x: intersection.position.x,
      y: intersection.position.y
    };

    let dividerCenterSpacing = 5;
    let dividerEdgeSpacing = 2;

    if (intersection.connectionSides.includes(CONSTANTS.DIRECTIONS.UP)) {
      dividerPathString += ` M ${intersectionCenter.x} ${intersectionCenter.y - dividerCenterSpacing} L ${intersectionCenter.x} ${intersectionTop + dividerEdgeSpacing}`;
    } else {
      strokePathString += ` M ${intersectionLeft} ${intersectionTop} L ${intersectionRight} ${intersectionTop}`;
    }

    if (intersection.connectionSides.includes(CONSTANTS.DIRECTIONS.RIGHT)) {
      dividerPathString += ` M ${intersectionCenter.x + dividerCenterSpacing} ${intersectionCenter.y} L ${intersectionRight - dividerEdgeSpacing} ${intersectionCenter.y}`;
    } else {
      strokePathString += ` M ${intersectionRight} ${intersectionTop} L ${intersectionRight} ${intersectionBottom}`;
    }

    if (intersection.connectionSides.includes(CONSTANTS.DIRECTIONS.DOWN)) {
      dividerPathString += ` M ${intersectionCenter.x} ${intersectionCenter.y + dividerCenterSpacing} L ${intersectionCenter.x} ${intersectionBottom - dividerEdgeSpacing}`;
    } else {
      strokePathString += ` M ${intersectionLeft} ${intersectionBottom} L ${intersectionRight} ${intersectionBottom}`;
    }

    if (intersection.connectionSides.includes(CONSTANTS.DIRECTIONS.LEFT)) {
      dividerPathString += ` M ${intersectionCenter.x - dividerCenterSpacing} ${intersectionCenter.y} L ${intersectionLeft + dividerEdgeSpacing} ${intersectionCenter.y}`;
    } else {
      strokePathString += ` M ${intersectionLeft} ${intersectionTop} L ${intersectionLeft} ${intersectionBottom}`;
    }

    strokePathString.trim();
    dividerPathString.trim();

    let intersectionStroke = new fabric.Path(strokePathString, {
      fill: "transparent",
      stroke: this.layoutData.roadSettings.strokeColor,
      strokeWidth: strokeWidth
    });

    let backgroundRect = new fabric.Rect({
      width: outerSize,
      height: outerSize,
      top: intersection.position.y,
      left: intersection.position.x,
      fill: this.layoutData.roadSettings.roadColor,
      originX: "center",
      originY: "center"
    });

    let dividerPath = new fabric.Path(dividerPathString, {
      fill: "",
      stroke: this.layoutData.roadSettings.dividerColor,
      strokeDashArray: this.layoutData.roadSettings.dividerDashArray,
      // strokeDashArray: [2, 2],
      strokeWidth: dividerWidth
    });

    let intersectionGroupObj = new fabric.Group([backgroundRect, intersectionStroke], {
      hasBorders: false,
      hasControls: false,
      selectable: false,
      evented: false,
      reference: false
    });

    this.roadMapGroup.add(intersectionGroupObj);

    intersectionGroup.backgroundObj = backgroundRect;
    intersectionGroup.strokeObj = intersectionStroke;
    // intersectionGroup.dividerObj = dividerPath;
    intersectionGroup.groupObj = intersectionGroupObj;

    this.intersections.push(intersectionGroup);
  }

  drawRoad(road) {
    this.businessModel.drawDebugInfo(road.key, road.connections[0].startPosition, "#0000FF");

    return new Promise((resolve, reject) => {
      let roadGroup = {
        data: road,
        crossings: []
      };

      /*
        Path commands (https://developer.mozilla.org/en-US/docs/Web/SVG/Tutorial/Paths):
        M = move to
        L = line to
          H = draw horizontal line
          V = draw vertical line
        C = cubic curve (2 control points, 1 end point)
        Q = quadratic curve (1 control point, 1 end point)
       */

      let outerWidth = this.layoutData.roadSettings.outerWidth,
        innerWidth = this.layoutData.roadSettings.innerWidth;

      log.debug("Drawing road '%s'", road.key);

      for (let i = 0; i < road.connections.length; i++) {
        let connection = road.connections[i];
        let startPosition = connection.startPosition;
        let endPosition = connection.endPosition;

        let pathString = RoadUtils.getPathStringForNavigation(startPosition, connection.navigation, endPosition, innerWidth, outerWidth);

        // log.debug("Path string for road: %s", pathString);

        let fabricObjects = this.drawConnection(pathString, {
          dividerWidth: this.layoutData.roadSettings.dividerWidth,
          innerWidth: this.layoutData.roadSettings.innerWidth,
          outerWidth: this.layoutData.roadSettings.outerWidth,

          dividerColor: this.layoutData.roadSettings.dividerColor,
          dividerDashArray: this.layoutData.roadSettings.dividerDashArray,

          backgroundColor: this.layoutData.roadSettings.roadColor,

          strokeColor: this.layoutData.roadSettings.strokeColor
        });


        roadGroup.dividerObj = fabricObjects.dividerObj;
        roadGroup.backgroundObj = fabricObjects.backgroundObj;
        roadGroup.strokeObj = fabricObjects.strokeObj;
        roadGroup.groupObj = fabricObjects.groupObj;
      }

      if (utils.isNotNull(road.crossings) && road.crossings.length > 0) {
        let crossingsRendered = 0;

        for (let i = 0; i < road.crossings.length; i++) {
          this.drawCrossing(road.crossings[i], roadGroup)
            .then((crossingObj) => {
              this.crossingsGroup.addWithUpdate(crossingObj);
              crossingsRendered++;
              checkRoadRendered.call(this, crossingsRendered);
            });
        }
      } else {
        checkRoadRendered.call(this, 0);
      }

      function checkRoadRendered(crossingsRendered) {
        /*if(utils.isNotNull(road.crossings))
        {
          log.debug("Road [%s] - crossings: %d/%d (rendered/required)", road.key, crossingsRendered, road.crossings.length);
        }
        else
        {
          log.debug("Road [%s] - has no crossings", road.key);
        }*/

        if (utils.isNull(road.crossings) || road.crossings.length === 0 || road.crossings.length === crossingsRendered) {
          this.roads.push(roadGroup);
          resolve(roadGroup);
        }
      }
    });
  }

  drawConnection(pathString, options) {
    let dividerWidth = options.dividerWidth,
      innerWidth = options.innerWidth,
      outerWidth = options.outerWidth,

      dividerColor = options.dividerColor,
      dividerDashArray = options.dividerDashArray,

      backgroundColor = options.backgroundColor,

      strokeColor = options.strokeColor;

    let group = [];

    let path,
      background,
      roadStroke;

    if (utils.isNotNull(dividerColor)) {
      path = new fabric.Path(pathString, {
        fill: "",
        stroke: dividerColor,
        strokeDashArray: dividerDashArray,
        // strokeLinejoin: "miter",
        // strokeMiterlimit: 3,
        strokeWidth: dividerWidth
      });
    }

    if (utils.isNotNull(backgroundColor)) {
      background = new fabric.Path(pathString, {
        fill: "transparent",
        stroke: backgroundColor,
        strokeWidth: innerWidth
      });
    }

    if (utils.isNotNull(strokeColor)) {
      roadStroke = new fabric.Path(pathString, {
        fill: "transparent",
        stroke: strokeColor,
        strokeWidth: outerWidth
      });
    }

    if (roadStroke) group.push(roadStroke);
    if (background) group.push(background);
    if (path) group.push(path);

    let roadGroupObj = new fabric.Group(group, {});

    let g = options.addToGroup || this.roadMapGroup;

    if (options.addWithUpdate) {
      g.addWithUpdate(roadGroupObj);
    } else {
      g.add(roadGroupObj);
    }

    return {
      dividerObj: path,
      backgroundObj: background,
      strokeObj: roadStroke,
      groupObj: roadGroupObj
    };
  }

  drawActiveConnection(pathString, options) {
    let dividerWidth = options.dividerWidth,
      innerWidth = options.innerWidth,
      dividerColor = options.dividerColor,
      backgroundColor = options.backgroundColor;

    let points = RoadUtils.pathToPoints(pathString, options.samples, options.pointsSamplePercentage);

    let divider,
      background;

    let startPoints = options.startEmpty ? [] : points;

    if (utils.isNotNull(dividerColor)) {
      divider = new fabric.Polyline(startPoints, {
        fill: "transparent",
        stroke: dividerColor,
        strokeWidth: dividerWidth,
        objectCaching: false
      });
    }

    if (utils.isNotNull(backgroundColor)) {
      background = new fabric.Polyline(startPoints, {
        fill: "transparent",
        stroke: backgroundColor,
        strokeWidth: innerWidth,
        objectCaching: false
      });
    }

    if (options.addWithUpdate) {
      this.activeRoadsGroup.addWithUpdate(background);
      this.activeRoadsGroup.addWithUpdate(divider);
    } else {
      this.activeRoadsGroup.add(background);
      this.activeRoadsGroup.add(divider);
    }

    // Make background not overlap divider of other active roads
    background.sendToBack();

    return {
      dividerObj: divider,
      backgroundObj: background,
      points: points
    };
  }

  drawCrossing(position, roadGroup) {
    return this.businessModel.loadSvg(this.layoutData.roadSettings.crossingIcon)
      .then((fabricObj) => {
        fabricObj.originX = "center";
        fabricObj.originY = "center";

        fabricObj.top = position.y;
        fabricObj.left = position.x;

        fabricObj.scaleToWidth(this.layoutData.roadSettings.innerWidth - 2);
        fabricObj.angle = position.rotation;

        roadGroup.crossings.push(fabricObj);

        return fabricObj;
      })
      .catch((error) => {
        log.error("Failed to load crossings icon - %o", error);
      });
  }

  drawArrows() {
    for (let i = 0; i < this.layoutData.roads.length; i++) {
      if (utils.isNotNull(this.layoutData.roads[i].arrows) && this.layoutData.roads[i].arrows.length > 0) {
        for (let j = 0; j < this.layoutData.roads[i].arrows.length; j++) {
          let arrowInfo = this.layoutData.roads[i].arrows[j];

          let arrowGroup;

          if (this.arrowGroups.hasOwnProperty(arrowInfo.group)) {
            arrowGroup = this.arrowGroups[arrowInfo.group];
          } else {
            arrowGroup = {};

            arrowGroup.fabricObj = new fabric.Group([], {
              hasBorders: false,
              hasControls: false,
              selectable: false,
              evented: false,
              reference: false,
              opacity: 0
            });

            this.canvas.add(arrowGroup.fabricObj);

            this.arrowGroups[arrowInfo.group] = arrowGroup;
          }

          arrowGroup.fabricObj.addWithUpdate(this.drawArrow(arrowInfo));
        }
      }
    }
  }

  drawArrow(position) {
    let tmpArrow = fabric.util.object.clone(this._templateRoadArrow);
    tmpArrow.left = position.x;
    tmpArrow.top = position.y;
    tmpArrow.angle = position.rotation;
    tmpArrow.visible = true;
    // tmpArrow.opacity = 0;

    // tmpArrow.scaleToHeight(this.layoutData.roadSettings.dividerWidth * 3);

    return tmpArrow;
  }

  previewRoad(startPoint, intersections, endPoint, animationOptions) {
    animationOptions = utils.isNotNull(animationOptions) ? animationOptions : {};

    let foundRoad = this.findRoad(startPoint, endPoint, intersections);

    if (utils.isNotNull(foundRoad)) {
      log.debug("Found road: %o", foundRoad);

      let connections = RoadUtils.getConnectionsForRoad(foundRoad.data, startPoint, endPoint, intersections, this.layoutData);

      log.info("Connection info for road [%s]\nStartPoint: %s --- EndPoint: %s --- Intersections: %o\nConnections: %o", foundRoad.data.key, startPoint, endPoint, intersections, connections);

      let pathString = this.createPathForConnections(connections);

      this.drawConnection(pathString, {
        dividerWidth: animationOptions.roadDividerWidth || this.layoutData.roadSettings.dividerWidth,
        innerWidth: this.layoutData.roadSettings.innerWidth,

        dividerColor: animationOptions.roadDividerColor || this.layoutData.roadSettings.dividerColorActive,
        backgroundColor: animationOptions.roadBackgroundColor || this.layoutData.roadSettings.roadColorActive,

        addWithUpdate: true,
        startEmpty: false,

        // Active roads need to be animated so we can't just use path, we use PolyLine instead. This gives us control
        // over all the points but the disadvantage of this is that the road might not be as nicely drawn as a path.
        // Use 20% of the total path length as sample count (the more samples the more exact the road will be, 20% seems to be enough).
        // A higher sample count = less performance since I assume PolyLine is less performant than Path.
        // use 'samples' instead of 'pointsSamplePercentage' if you want to use an exact sample count
        samples: null,
        pointsSamplePercentage: 0.2,
        addToGroup: this.previewGroup
      });

      this.previewGroup.bringToFront();
      return this.previewGroup;
    } else {
      log.error("No road found for start point [%s] and end point [%s] with intersections [%o]", startPoint, endPoint, intersections);
    }
  }

  /**
   * waitOnPrevious: (boolean) waits on previous road to complete its animation before starting its own animation
   */
  activateRoads(roads, animationOptions, waitOnPrevious) {
    async function activateRoadsWait(context) {
      for (let i = 0; i < roads.length; i++) {
        await context.activateRoad(roads[i].startPoint, roads[i].intersections, roads[i].endPoint, animationOptions);
      }
    }

    if (waitOnPrevious) {
      return activateRoadsWait(this);
    } else {
      return new Promise((resolve, reject) => {
        let roadsActivated = 0;

        for (let i = 0; i < roads.length; i++) {
          this.activateRoad(roads[i].startPoint, roads[i].intersections, roads[i].endPoint, animationOptions)
            .then((data) => {
              if (++roadsActivated === roads.length) {
                resolve();
              }
            });
        }
      });
    }
  }

  activateRoad(startPoint, intersections, endPoint, animationOptions) {
    animationOptions = utils.isNotNull(animationOptions) ? animationOptions : {};

    return new Promise((resolve, reject) => {
      log.info("Activating road from '%s' to '%s'", startPoint, endPoint);

      let activeConnectionInfo = this.findActiveConnection(startPoint, endPoint, intersections);

      if (utils.isNotNull(activeConnectionInfo)) {
        // reject(new AppError({ exception: new Error("Road is already activated" )}));
        resolve();
        return;
      }

      let foundRoad = this.findRoad(startPoint, endPoint, intersections);

      if (utils.isNotNull(foundRoad)) {
        log.debug("Found road: %o", foundRoad);

        let connections = RoadUtils.getConnectionsForRoad(foundRoad.data, startPoint, endPoint, intersections, this.layoutData);

        log.info("Connection info for road [%s]\nStartPoint: %s --- EndPoint: %s --- Intersections: %o\nConnections: %o", foundRoad.data.key, startPoint, endPoint, intersections, connections);

        let pathString = this.createPathForConnections(connections);

        let activeConnectionGroup = this.drawActiveConnection(pathString, {
          dividerWidth: this.layoutData.roadSettings.dividerWidth,
          innerWidth: this.layoutData.roadSettings.innerWidth,

          dividerColor: this.layoutData.roadSettings.dividerColorActive,
          backgroundColor: this.layoutData.roadSettings.roadColorActive,

          addWithUpdate: true,
          startEmpty: true,

          // Active roads need to be animate so we can't just use path, we use PolyLine instead. This gives us control
          // over all the points but the disadvantage of this is that the road might not be as nicely drawn as a path
          // Use 20% of the total path length as sample count (the more samples the more exact the road will be, 20% seems to be enough).
          // A higher sample count = less performance since I assume PolyLine is less performant than Path.
          // use samples instead of pointsSamplePercentage if you want to use an exact sample count
          samples: null,
          pointsSamplePercentage: 0.2
        });

        log.debug("ActiveConnectionGroup: %o", activeConnectionGroup);

        if (utils.isNotNull(activeConnectionGroup)) {
          activeConnectionGroup.connections = connections;
          // Save parameters in activeConnectionGroup so we can easily find out if it's already active or to deactivate it.
          activeConnectionGroup.parameters = {startPoint, intersections, endPoint};
          this.activeConnections.push(activeConnectionGroup);

          this.animateRoadForActiveConnection(activeConnectionGroup, animationOptions).then(resolve).catch(reject);
        } else {
          log.error("Failed to draw active connection for path [%s]", pathString);
          reject(new AppError({exception: new Error("Failed to draw active connection for path")}));
        }
      } else {
        log.error("No road found for start point [%s] and end point [%s] with intersections [%o]", startPoint, endPoint, intersections);
        reject(new AppError({exception: new Error(utils.format("No road found for start point [{0}] and end point [{1}]", startPoint, endPoint))}));
      }
    });
  }

  createPathForConnections(connections) {
    let newConnection = connections.length === 1 ? connections[0] : RoadUtils.combineConnections(connections);

    log.debug("Merged connection: %o\nOriginals: %o", newConnection, connections);

    if (utils.isNotNull(newConnection)) {
      let outerWidth = this.layoutData.roadSettings.outerWidth,
        innerWidth = this.layoutData.roadSettings.innerWidth;

      let pathString = RoadUtils.getPathStringForNavigation(newConnection.startPosition, newConnection.navigation, newConnection.endPosition, innerWidth, outerWidth);

      log.debug("Path string for merged connections: %s", pathString);

      return pathString;
    } else {
      log.error("Failed to combine connections [%o]", connections);

      return null;
    }
  }

  deactivateAllRoads(animationOptions) {
    let allRoads = this.activeConnections.map(ar => ar.parameters);
    log.info("Deactivating all roads: %o", allRoads);
    this.deactivateRoads(allRoads, animationOptions, false);
  }

  deactivateRoads(roads, animationOptions, waitOnPrevious) {
    async function deactivateRoadsWait(context) {
      for (let i = 0; i < roads.length; i++) {
        await context.deactivateRoad(roads[i].startPoint, roads[i].intersections, roads[i].endPoint, animationOptions);
      }
    }

    if (waitOnPrevious) {
      return deactivateRoadsWait(this);
    } else {
      return new Promise((resolve, reject) => {
        let roadsDeactivated = 0;

        for (let i = 0; i < roads.length; i++) {
          this.deactivateRoad(roads[i].startPoint, roads[i].intersections, roads[i].endPoint, animationOptions)
            .then((data) => {
              if (++roadsDeactivated === roads.length) {
                resolve();
              }
            });
        }
      });
    }
  }

  deactivateRoad(startPoint, intersections, endPoint, animationOptions) {
    animationOptions = utils.isNotNull(animationOptions) ? animationOptions : {};

    return new Promise((resolve, reject) => {
      log.info("Deactivating road from '%s' to '%s'", startPoint, endPoint);

      let activeConnectionInfo = this.findActiveConnection(startPoint, endPoint, intersections);

      if (utils.isNull(activeConnectionInfo)) {
        reject(new AppError({exception: new Error("Road isn't active")}));
      } else {
        animationOptions = Object.assign({}, animationOptions, {inverse: true});

        this.activeConnections.splice(activeConnectionInfo.index, 1);
        this.animateRoadForActiveConnection(activeConnectionInfo.activeConnection, animationOptions).then(resolve).catch(reject);
      }
    });
  }

  findRoad(startPoint, endPoint, intersections) {
    let foundRoad = null;

    for (let i = 0; i < this.roads.length; i++) {
      let roadGroup = this.roads[i];
      let roadData = roadGroup.data;

      let includesAllIntersections = true;

      if (utils.isNotNull(intersections)) {
        for (let j = 0; j < intersections.length; j++) {
          if (!roadData.connects.includes(intersections[j])) {
            includesAllIntersections = false;
            break;
          }
        }
      }

      if (roadData.connects.includes(startPoint) && roadData.connects.includes(endPoint) && includesAllIntersections) {
        foundRoad = roadGroup;
        break;
      }
    }

    return foundRoad;
  }

  findActiveConnection(startPoint, endPoint, intersections) {
    for (let i = 0; i < this.activeConnections.length; i++) {
      let connectionParameters = this.activeConnections[i].parameters;

      if (connectionParameters.startPoint === startPoint && connectionParameters.endPoint === endPoint && utils.arraysAreEqual(intersections, connectionParameters.intersections)) {
        return {index: i, activeConnection: this.activeConnections[i]};
      }
    }

    return null;
  }

  animateRoadForActiveConnection(activeConnectionGroup, animationOptions) {
    animationOptions = utils.isNotNull(animationOptions) ? animationOptions : {};

    return new Promise((resolve, reject) => {
      log.debug("Animating road: %o --- animationOptions: %o", activeConnectionGroup, animationOptions);

      if (utils.isNull(activeConnectionGroup.arrows)) // && !animationOptions.inverse
      {
        activeConnectionGroup.arrows = [];
      }

      if (animationOptions.instant) {
        if (animationOptions.inverse) {
          while (activeConnectionGroup.arrows.length > 0) {
            let arrow = activeConnectionGroup.arrows.pop();
            this.activeRoadsGroup.removeWithUpdate(arrow);
          }

          activeConnectionGroup.arrows = null;

          this.activeRoadsGroup.removeWithUpdate(activeConnectionGroup.backgroundObj);
          this.activeRoadsGroup.removeWithUpdate(activeConnectionGroup.dividerObj);
        } else {
          activeConnectionGroup.backgroundObj.set({points: activeConnectionGroup.points});
          activeConnectionGroup.dividerObj.set({points: activeConnectionGroup.points});

          this.canvas.renderAll();

          activeConnectionGroup.backgroundObj.setCoords();
          activeConnectionGroup.dividerObj.setCoords();

          this.activeRoadsGroup.addWithUpdate();
        }

        this.showArrowGroup(activeConnectionGroup, animationOptions);

        this.canvas.renderAll();

        resolve(activeConnectionGroup);
      } else {
        if (animationOptions.inverse) this.showArrowGroup(activeConnectionGroup, animationOptions);

        let lastPointIndexAdded = -1;

        this.animationsRunning++;
        fabric.util.animate({
          startValue: 0,
          endValue: activeConnectionGroup.points.length - 1,
          duration: activeConnectionGroup.points.length * 10,
          onChange: (value) => {
            value = Math.ceil(value);

            if (value > lastPointIndexAdded) {
              // log.debug("lastPointIndexAdded: %d --- Value: %d\nspliceStart: %d --- spliceLength: %d", lastPointIndexAdded, value, lastPointIndexAdded + 1, value - upToPoint);

              if (animationOptions.inverse) {
                activeConnectionGroup.backgroundObj.points.splice(0, value - lastPointIndexAdded);
                activeConnectionGroup.dividerObj.points.splice(0, value - lastPointIndexAdded);
              } else {
                let pointsToConcat = activeConnectionGroup.points.slice(lastPointIndexAdded + 1, value + 1);

                activeConnectionGroup.backgroundObj.points = activeConnectionGroup.backgroundObj.points.concat(pointsToConcat);
                activeConnectionGroup.dividerObj.points = activeConnectionGroup.dividerObj.points.concat(pointsToConcat);
              }

              this.canvas.renderAll();

              lastPointIndexAdded = value;
            }
          },
          onComplete: () => {
            this.animationsRunning--;
            this.setShouldAbortAnimations();

            if (animationOptions.inverse) {
              while (activeConnectionGroup.arrows.length > 0) {
                let arrow = activeConnectionGroup.arrows.pop();
                this.activeRoadsGroup.removeWithUpdate(arrow);
              }

              activeConnectionGroup.arrows = null;

              this.activeRoadsGroup.removeWithUpdate(activeConnectionGroup.backgroundObj);
              this.activeRoadsGroup.removeWithUpdate(activeConnectionGroup.dividerObj);
            } else {
              activeConnectionGroup.backgroundObj.setCoords();
              activeConnectionGroup.dividerObj.setCoords();

              this.showArrowGroup(activeConnectionGroup, animationOptions);
            }

            this.canvas.renderAll();

            log.debug("Road animation complete");

            if (this.shouldAbortAnimations) {
              this.setShouldAbortAnimations();
              reject();
            } else {
              resolve(activeConnectionGroup);
            }
          },
          abort: () => {
            return this.shouldAbortAnimations;
          }
        });
      }
    });
  }

  showArrowGroup(activeConnectionGroup, animationOptions) {
    if (animationOptions.instant || true) {
      // Hide arrow
      for (let i = 0; i < activeConnectionGroup.connections.length; i++) {
        let arrowGroup = activeConnectionGroup.connections[i].arrowGroup;

        if (this.arrowGroups.hasOwnProperty(arrowGroup)) {
          this.arrowGroups[arrowGroup].fabricObj.opacity = animationOptions.inverse ? 0 : 1;
          /*for(let j = 0; j < this.arrowGroups[arrowGroup].fabricObj._objects.length; j++)
          {
            let arrowObj = this.arrowGroups[arrowGroup].fabricObj._objects[j];
            arrowObj.opacity = animationOptions.inverse ? 0 : 1;
          }*/
        }
      }
    } else {
      if (animationOptions.inverse) {

      } else {

      }
    }
  }

  setShouldAbortAnimations() {
    log.info("Should abort ? %s --- animationsRunning ? %d", this.shouldAbortAnimations, this.animationsRunning);

    if (this.shouldAbortAnimations && this.animationsRunning <= 0) {
      this.shouldAbortAnimations = false;
    }
  }

  abortAnimations() {
    if (this.animationsRunning > 0) {
      this.shouldAbortAnimations = true;
    }
  }
}

export default RoadMap;



// WEBPACK FOOTER //
// ./app/js/components/business-model/road-map.js